import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/hooks/use-theme';
import { JSX, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import CheckoutPage from '@/components/CheckoutPage';
import SuccessPage from '@/components/SuccessPage';
import ExpiredPage from '@/components/ExpiredPage';
import DashboardPage from '@/components/DashboardPage';
import NotFoundPage from '@/components/NotFoundPage';
import { startInit, subscribeInit, type InitStatus } from '@/lib/poofClient';

function App(): JSX.Element {
  const [initStatus, setInitStatus] = useState<InitStatus>('idle');
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Poofy Pay';
    const metaAppTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (metaAppTitle) {
      metaAppTitle.setAttribute('content', 'Poofy Pay');
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeInit((status, error) => {
      setInitStatus(status);
      setInitError(error?.message ?? null);
    });

    void startInit().catch(() => {
      // subscribeInit surfaces the same error for rendering.
    });

    return unsubscribe;
  }, []);

  const initGate = initStatus !== 'ready';

  return (
    <ThemeProvider>
      <div
        id='app-container'
        className='relative min-h-screen flex flex-col bg-background'
      >
        <main id='app-main' className='flex-1'>
          {initGate ? (
            <div className='flex min-h-screen items-center justify-center bg-background px-6 text-center text-muted-foreground'>
              {initStatus === 'error' ? initError ?? 'Poof config failed to load.' : 'Loading...'}
            </div>
          ) : (
            <Routes>
              <Route path='/' element={<Navigate to='/dashboard' replace />} />
              <Route path='/pay/:sessionId' element={<CheckoutPage />} />
              <Route path='/pay/:sessionId/success' element={<SuccessPage />} />
              <Route path='/pay/:sessionId/expired' element={<ExpiredPage />} />
              <Route path='/dashboard' element={<DashboardPage />} />
              <Route path='*' element={<NotFoundPage />} />
            </Routes>
          )}
        </main>
        <Toaster />
      </div>
    </ThemeProvider>
  );
}

export default App;
